/**
 * "내가 연 회의실" — §9.2.
 *
 * **이 기능의 출발점이다.** 사용자는 회의가 끝나 브라우저를 닫았고, 방은
 * 설계대로 살아 있었다(§9 재접속 유예). 문제는 그 사실을 볼 방법도, 자기
 * 손으로 치울 방법도 없었다는 것이다 — 관리자에게 물어서야 알았다.
 *
 * 그래서 로그인한 사람의 첫 화면에 자기가 연 방을 보여 주고, 같은 자리에서
 * 닫을 수 있게 한다. 자기가 만든 것은 자기가 치울 수 있어야 한다.
 *
 * 열려 있는 방이 없으면 **아무것도 그리지 않는다** — 평소에는 없는 문제로
 * 화면을 채우지 않는다.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Card, CardContent } from '@cupia/ui/card'
import { RoomTable } from './room-table'
import { listMyRooms, resumeHost, type RoomSummary } from '../lib/rooms-admin'
import { saveHostCredential } from '../lib/host-credential'
import { ApiError } from '../lib/api'

/** 재입장 실패 안내(DEF-131) — 서버 상태 코드별. */
export const RESUME_MESSAGES = {
  forbidden: '이 계정이 만든 방만 다시 호스트로 들어갈 수 있습니다.',
  unauthenticated: '로그인이 만료되었습니다. 다시 로그인해 주세요.',
  failed: '호스트로 돌아가지 못했습니다.',
} as const

export interface MyRoomsPanelProps {
  onNotify?: (message: string) => void
}

export function MyRoomsPanel({ onNotify }: MyRoomsPanelProps): React.ReactElement {
  const [rooms, setRooms] = useState<RoomSummary[]>([])
  const [loaded, setLoaded] = useState(false)
  const [resumeError, setResumeError] = useState<string | null>(null)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    try {
      const res = await listMyRooms()
      // 종료된 방은 굳이 보여 주지 않는다 — 소유자에게 필요한 것은
      // "아직 열려 있는 내 방" 이다(관리자 화면은 전부 보여 준다).
      // 응답 모양은 믿지 않는다: 이 패널은 랜딩 화면에 얹혀 있어서
      // 여기서 던지면 로그인·방 만들기까지 함께 사라진다.
      const list = Array.isArray(res?.rooms) ? res.rooms : []
      setRooms(list.filter((r) => r.state !== 'closed'))
    } catch {
      // 비로그인이면 401 — 패널을 그리지 않는다(빈 목록 유지).
      setRooms([])
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * 호스트로 돌아가기(DEF-131) — 탭을 닫아 잃은 자격을 계정으로 다시 받아
   * 개설 때와 같은 함수로 저장하고 호스트 화면으로 간다.
   */
  const onResume = useCallback(
    async (r: RoomSummary) => {
      setResumeError(null)
      try {
        const res = await resumeHost(r.room)
        saveHostCredential(res)
        void navigate('/host')
      } catch (e) {
        const status = e instanceof ApiError ? e.status : 0
        setResumeError(
          status === 403
            ? RESUME_MESSAGES.forbidden
            : status === 401
              ? RESUME_MESSAGES.unauthenticated
              : RESUME_MESSAGES.failed,
        )
      }
    },
    [navigate],
  )

  // 문제가 없으면 조용히 있는다.
  if (!loaded || rooms.length === 0) return <></>

  return (
    <Card className="md:col-span-2" data-testid="my-rooms">
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">내가 연 회의실</span>
          <span className="text-(length:--text-sm-size) text-[var(--color-muted-foreground)]">
            브라우저를 닫아도 회의실은 재접속을 위해 잠시 열려 있습니다. 끝난
            회의라면 파기해 주세요.
          </span>
        </div>
        {resumeError && (
          <span
            role="alert"
            data-testid="my-rooms-resume-error"
            className="text-(length:--text-sm-size) text-[var(--color-destructive)]"
          >
            {resumeError}
          </span>
        )}
        <RoomTable
          rooms={rooms}
          onResume={(r) => void onResume(r)}
          onRefresh={() => void load()}
          onNotify={onNotify}
          showOwner={false}
          emptyTitle="열려 있는 회의실이 없습니다"
          emptyDescription="내가 연 회의실이 여기에 표시됩니다."
        />
      </CardContent>
    </Card>
  )
}
